import { Individual } from '../../../utils';
import { Autocomplete, Avatar, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useDebounce } from 'use-debounce';
import { getIndividuals } from '../../../api';

interface IndividualsAutocompleteProps {
    organization: string;
    onChange: (individuals: any) => void;
    value: Individual[] | Individual | null;
    multiple?: boolean;
    size?: 'small' | 'medium';
    excludeUuids?: string[];
}

function IndividualsAutocomplete({
    onChange,
    organization,
    value,
    multiple = true,
    size = 'medium',
    excludeUuids = [],
}: IndividualsAutocompleteProps) {
    const [loading, setLoading] = useState(false);
    const [individualSearchValue, setIndividualSearchValue] = useState<string>('');
    const [debouncedSearchValue] = useDebounce(individualSearchValue, 300);
    const [individuals, setIndividuals] = useState<Individual[]>([]);

    function loadIndividuals(
        params: { limit?: number; 'filter[name]'?: string; 'filter[organization]'?: string } = {
            limit: 100,
        },
    ) {
        setLoading(true);
        getIndividuals(params)
            .then((individualsResponse) => {
                setIndividuals(individualsResponse.data.data);
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        let params: { limit?: number; 'filter[name]'?: string; 'filter[organization]'?: string } = {
            limit: 200,
            'filter[organization]': organization,
        };
        if (debouncedSearchValue) {
            params['filter[name]'] = debouncedSearchValue;
            loadIndividuals(params);
        }
    }, [debouncedSearchValue, organization]);

    return (
        <Autocomplete
            freeSolo
            size={size}
            multiple={multiple}
            id="individual-autocomplete"
            options={individuals}
            selectOnFocus={false}
            inputValue={individualSearchValue}
            onInputChange={(_, newInputValue) => {
                setIndividualSearchValue(newInputValue);
            }}
            value={value}
            loading={loading}
            onChange={(_, value) => {
                if (typeof value !== 'string') {
                    onChange(value as Individual[]);
                }
            }}
            getOptionDisabled={(option: Individual | string) =>
                typeof option !== 'string' && excludeUuids.includes(option.uuid)
            }
            filterOptions={(options) => options}
            getOptionLabel={(option: Individual | string) => {
                if (typeof option !== 'string') {
                    return option.name as string;
                }
                return option;
            }}
            isOptionEqualToValue={(option: Individual, value) => {
                if (typeof value === 'string') {
                    return false;
                }
                if (option.uuid) {
                    return option.uuid === value?.uuid;
                }
                return false;
            }}
            style={{
                width: '100%',
                maxWidth: 375,
                minWidth: 250,
                margin: 2,
            }}
            renderOption={(props, option) => (
                <li {...props}>
                    <Avatar src={option.profile_photo ?? ''} sx={{ mr: 1 }} />
                    <div>
                        <div>{option.name}</div>
                        {option.managing_user && (
                            <div style={{ fontSize: '0.75rem', color: '#888' }}>
                                {option.managing_user.name}
                                {option.managing_user.user_name
                                    ? ` (@${option.managing_user.user_name})`
                                    : ''}
                            </div>
                        )}
                    </div>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Choose Athlete(s)"
                    variant="outlined"
                    color={'primary'}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="primary" size={24} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}

export default IndividualsAutocomplete;
